import { useEffect, useRef, useState } from 'react';
import type { ExperienceTheme } from '@aeonia/gaia-client';
import { SPEAK_CHIP, STOP_CHIP, TYPE_CHIP } from '@aeonia/gaia-client';

type ChipRowProps = {
  labels: string[];
  onSelect: (label: string) => void;
  /** Sends whatever the player typed into the Type chip's input. */
  onSendText?: (text: string) => void;
  disabled?: boolean;
  micChipEnabled?: boolean;
  theme: ExperienceTheme;
  /** display.choice options: each on its own full-width row above the answer buttons (Jason, 2026-09-28, option B). */
  choices?: string[];
};

/**
 * The voice button is a microphone, not a word (Jason: "a microphone button and
 * a speak button are redundant, drop the speak button"). The word stays as the
 * accessible name; while listening the button also reads STOP.
 */
function MicLabel({ label }: { label: string }) {
  const listening = label === STOP_CHIP;
  return (
    <span className="glasses-mic-label">
      <svg className="glasses-mic-icon" width="36" height="51" viewBox="0 0 24 34" aria-hidden="true">
        <rect x="6" y="2" width="12" height="20" rx="6" fill={listening ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="3" />
        <path d="M2 16 a10 10 0 0 0 20 0" fill="none" stroke="currentColor" strokeWidth="3" />
        <path d="M12 26 V32 M7 32 H17" fill="none" stroke="currentColor" strokeWidth="3" />
      </svg>
      {listening ? <span className="glasses-mic-stop">{label}</span> : <span className="glasses-visually-hidden">{label}</span>}
    </span>
  );
}

/**
 * The Type chip's keyboard icon. Icons on this display are inline SVG with
 * strokes 4px or heavier, never an icon font (style ruling). The label stays
 * for screen readers only, the same pattern MicLabel uses.
 */
function TypeLabel() {
  return (
    <span className="glasses-mic-label">
      <svg className="glasses-type-icon" width="46" height="34" viewBox="0 0 32 24" aria-hidden="true">
        <rect x="2" y="2" width="28" height="20" rx="3" fill="none" stroke="currentColor" strokeWidth="4" />
        <path d="M8 9 H8.01 M14 9 H14.01 M20 9 H20.01 M24 9 H24.01 M9 15 H23" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
      </svg>
      <span className="glasses-visually-hidden">{TYPE_CHIP}</span>
    </span>
  );
}

export default function ChipRow({ labels, onSelect, onSendText, disabled, micChipEnabled, theme, choices = [] }: ChipRowProps) {
  const firstChipRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mirrorRef = useRef<HTMLSpanElement>(null);
  const isMicChip = labels[0] === SPEAK_CHIP || labels[0] === STOP_CHIP;
  const [typingOpen, setTypingOpen] = useState(false);
  const [draft, setDraft] = useState('');
  // The caret is a steady block, not the browser's native blinking one
  // (blinking is continuous animation, which the display rules forbid — spec
  // section 5). caret-color: transparent hides the native caret on the input;
  // this measures the typed text against a hidden mirror span of the same
  // font so the block can be positioned at the end of it, clamped to the
  // field's visible width once the text overflows and scrolls.
  const [caretLeft, setCaretLeft] = useState(24);
  const [inputFocused, setInputFocused] = useState(false);
  useEffect(() => {
    if (!typingOpen) return;
    const mirrorWidth = mirrorRef.current?.getBoundingClientRect().width ?? 0;
    const maxLeft = 552 - 24 - 12; // field width minus right padding minus caret width
    setCaretLeft(Math.min(24 + mirrorWidth, maxLeft));
  }, [draft, typingOpen]);

  // Only the focused element is lit (style guide: "one lit element at a
  // time"). When some other action already has focus on arrival — the nav
  // card's own "On my way" fallback is the action that step needs — this
  // row must not steal it, so both focus effects below check for that first.
  // Coordinator, 2026-09-29, from the denied-fallback screenshot: ChipRow
  // mounts after NavigationOverlay in GlassesApp.tsx, so without this guard
  // its mount effect ran second and always won the race, lighting the mic
  // chip instead of the fallback button it should have deferred to.
  function otherActionAlreadyFocused(): boolean {
    const active = document.activeElement;
    return active instanceof HTMLElement && active.classList.contains('focusable') && !active.closest('.glasses-chip-row');
  }

  useEffect(() => {
    if (otherActionAlreadyFocused()) return;
    firstChipRef.current?.focus();
  }, []);

  // Once the chips go from disabled back to enabled (a talk reply arrived, or
  // a retry chip appeared), focus lands back on the first chip so the next
  // pinch/Enter is never swallowed by a stray focused-but-disabled control.
  // The mic chip has its own enable channel, so it also earns focus when it
  // turns enabled even while the other chips stay disabled.
  useEffect(() => {
    if (otherActionAlreadyFocused()) return;
    if (!disabled || (isMicChip && micChipEnabled)) firstChipRef.current?.focus();
  }, [disabled, micChipEnabled, labels[0]]);

  // A new question (a different chip set) closes any input the player left
  // open on the previous step, so a stale reply can never be sent to the
  // wrong step.
  useEffect(() => {
    setTypingOpen(false);
    setDraft('');
  }, [labels.join('|')]);

  function submit(): void {
    const trimmed = draft.trim();
    if (!trimmed || disabled) return;
    onSendText?.(trimmed);
    setDraft('');
    setTypingOpen(false);
  }

  function closeInput(): void {
    setDraft('');
    setTypingOpen(false);
  }

  // Enter/Escape are handled here and stopped from bubbling so the page's own
  // Enter-starts-a-session listener and the d-pad's global arrow/Enter
  // handler never see keystrokes meant for the text field.
  function onInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      submit();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeInput();
    }
  }

  if (typingOpen) {
    return (
      <div className="glasses-chip-row glasses-type-row">
        <div className={draft.length > 0 ? 'glasses-type-input-wrap glasses-type-input-wrap-lit' : 'glasses-type-input-wrap'}>
          <input
            ref={inputRef}
            type="text"
            className="focusable glasses-type-input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onInputKeyDown}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            autoFocus
            aria-label="Type your reply"
            placeholder="Type your reply"
          />
          {/* Hidden mirror, same font as the input, used only to measure the
              typed text's width so the static caret block can track it. */}
          <span ref={mirrorRef} className="glasses-type-mirror" aria-hidden="true">
            {draft}
          </span>
          {inputFocused && (
            <span className="glasses-type-caret" style={{ left: `${caretLeft}px` }} aria-hidden="true" />
          )}
        </div>
        <button
          type="button"
          className="focusable glasses-chip glasses-send-chip"
          disabled={disabled || !draft.trim()}
          onClick={submit}
        >
          Send
        </button>
      </div>
    );
  }

  // Nothing to answer with: render nothing, so an empty row never takes layout space.
  if (labels.length === 0 && choices.length === 0) return null;

  const filled = theme.glasses.highlight === 'filled';
  const split = labels.length === 2;
  const baseClass = filled ? 'focusable glasses-chip glasses-chip-filled' : 'focusable glasses-chip';

  return (
    <div className="glasses-chip-row">
      {choices.map((choice, index) => (
        <div key={`choice-${choice}`} className="glasses-choice-row">
          <button
            type="button"
            ref={index === 0 ? firstChipRef : undefined}
            disabled={disabled}
            className={baseClass}
            onClick={() => onSelect(choice)}
          >
            {choice}
          </button>
        </div>
      ))}
    <div className={split ? 'glasses-answer-row glasses-chip-row-split' : 'glasses-answer-row'}>
      {labels.map((label, index) => {
        const isType = label === TYPE_CHIP;
        const chipDisabled = index === 0 && isMicChip ? disabled && !micChipEnabled : disabled;
        return (
          <button
            key={label}
            type="button"
            ref={index === 0 && choices.length === 0 ? firstChipRef : undefined}
            disabled={chipDisabled}
            className={split ? `${baseClass} glasses-chip-half` : baseClass}
            onClick={() => (isType ? setTypingOpen(true) : onSelect(label))}
          >
            {label === SPEAK_CHIP || label === STOP_CHIP ? <MicLabel label={label} /> : isType ? <TypeLabel /> : label}
          </button>
                );
      })}
    </div>
    </div>
  );
}
